/**
 * Currency utility — all monetary values in the app are stored as
 * minor units (cents). e.g. $20.00 → 2000, €18.50 → 1850.
 * JPY has no minor units, so ¥500 → 500.
 */

const ZERO_DECIMAL = new Set(['JPY', 'KRW', 'VND', 'BIF', 'GNF']);

/** Cents → display amount (divides by 100 for normal currencies). */
export function centsToAmount(cents, currencyCode) {
    if (cents == null) return 0;
    return ZERO_DECIMAL.has(currencyCode) ? Number(cents) : Number(cents) / 100;
}

/** Display amount → cents (multiplies by 100 for normal currencies). */
export function amountToCents(amount, currencyCode) {
    if (amount == null) return 0;
    return ZERO_DECIMAL.has(currencyCode)
        ? Math.round(Number(amount))
        : Math.round(Number(amount) * 100);
}

/**
 * Format a cents value as a localized currency string.
 * formatCurrency(2000, 'USD') → "$20.00"
 * formatCurrency(500000, 'AMD') → "֏5,000.00"
 */
export function formatCurrency(cents, currencyCode = 'EUR') {
    const amount = centsToAmount(cents, currencyCode);
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currencyCode,
        }).format(amount);
    } catch {
        const dec = ZERO_DECIMAL.has(currencyCode) ? 0 : 2;
        return `${currencyCode} ${amount.toFixed(dec)}`;
    }
}

/**
 * Convert cents from one currency to another.
 * rates must be relative to EUR (e.g. { USD: 1.08, AMD: 420, GBP: 0.86 }).
 * Returns cents in toCurrency.
 */
export function convertCents(cents, fromCurrency, toCurrency, rates) {
    if (!cents) return 0;
    if (!rates || fromCurrency === toCurrency) return Number(cents);
    const fromRate = fromCurrency === 'EUR' ? 1 : (rates[fromCurrency] ?? 1);
    const toRate   = toCurrency   === 'EUR' ? 1 : (rates[toCurrency]   ?? 1);
    return Math.round((Number(cents) / fromRate) * toRate);
}

/**
 * Exchange rate multiplier: how many toCurrency units per 1 fromCurrency unit.
 * Used for storing on Bill at booking time.
 */
export function getExchangeRate(fromCurrency, toCurrency, rates) {
    if (!rates || fromCurrency === toCurrency) return 1;
    const fromRate = fromCurrency === 'EUR' ? 1 : (rates[fromCurrency] ?? 1);
    const toRate   = toCurrency   === 'EUR' ? 1 : (rates[toCurrency]   ?? 1);
    return toRate / fromRate;
}
